<script lang="ts">
  import { untrack } from 'svelte';
  import { api } from '../../lib/api';
  import type { AudioIoConfig, TruckSink } from '../../../../shared/src/types';
  import { clone, type Devices, type Overview } from './types';

  let { data, reload }: { data: Overview; reload: () => void } = $props();
  // Draft starts from the config at open time; the 2 s overview poll must not overwrite edits.
  let draft = $state<AudioIoConfig>(untrack(() => clone(data.audio)));
  let devices = $state<Devices | null>(null);
  let newGroup = $state('');
  let msg = $state<string | null>(null);
  let error = $state<string | null>(null);

  void api.get<Devices>('/api/admin/devices').then((d) => (devices = d));

  function addSink(kind: TruckSink['kind']) {
    draft.truckOutput.sinks.push(
      kind === 'decklink' ? { kind, decklinkDevice: 0, decklinkMode: '1080i50', outputChannels: 8, channelPair: [7, 8] }
      : kind === 'ndi' ? { kind, ndiName: 'Talkback Out' }
      : kind === 'alsa' ? { kind, device: 'default', outputChannels: 2, channelPair: [1, 2] }
      : { kind, device: '' },
    );
  }

  async function save() {
    error = msg = null;
    try {
      draft = await api.put<AudioIoConfig>('/api/admin/audio', draft);
      msg = 'Applied.';
      reload();
    } catch (err) {
      error = (err as Error).message;
    }
  }
</script>

<div class="admin-grid">
  <section class="panel">
    <h3>Party line</h3>
    <div class="form">
      <label>Operators talk to
        <select bind:value={draft.partyLineScope}>
          <option value="all">everyone (single party line)</option>
          <option value="group">their group + directors + truck</option>
        </select>
      </label>
      <label>Duck incoming talk while talking (dB) <input type="number" max="0" bind:value={draft.duckDb} /></label>
      <div class="wide">
        <div class="muted">Groups</div>
        <div class="row">
          {#each draft.groups as g, i (g)}<span class="badge">{g} <button class="x" onclick={() => draft.groups.splice(i, 1)}>×</button></span>{/each}
          <input bind:value={newGroup} placeholder="e.g. Cameras" style="width:140px" />
          <button class="small" onclick={() => { if (newGroup.trim() && !draft.groups.includes(newGroup.trim())) draft.groups.push(newGroup.trim()); newGroup = ''; }}>Add</button>
        </div>
      </div>
    </div>
  </section>

  <section class="panel">
    <h3>Truck talkback input</h3>
    <p class="muted">Physical talkback (e.g. 4-wire from the intercom matrix) heard by everyone.</p>
    <div class="form">
      <label>Source
        <select bind:value={draft.truckInput.kind}>
          <option value="none">none</option><option value="alsa">ALSA device</option><option value="pulse">PipeWire/PulseAudio</option>
          <option value="decklink">DeckLink embedded audio</option><option value="source-pair">Channel pair of a source</option>
          <option value="ndi">NDI audio</option><option value="test">Test beeps</option>
        </select>
      </label>
      <label>Label <input bind:value={draft.truckInput.label} /></label>
      <label>Gain dB <input type="number" bind:value={draft.truckInput.gainDb} /></label>
      {#if draft.truckInput.kind === 'alsa' || draft.truckInput.kind === 'pulse'}
        <label class="wide">Device
          <input list="asrc" bind:value={draft.truckInput.device} placeholder={draft.truckInput.kind === 'alsa' ? 'hw:1,0' : 'default'} />
          <datalist id="asrc">{#each devices?.audioSources ?? [] as d (d.device + d.factory)}<option value={d.device}>{d.name} ({d.factory})</option>{/each}</datalist>
        </label>
      {/if}
      {#if draft.truckInput.kind === 'decklink'}
        <label>DeckLink device <input type="number" bind:value={draft.truckInput.decklinkDevice} /></label>
      {/if}
      {#if draft.truckInput.kind === 'ndi'}
        <label class="wide">NDI source <input list="ndis" bind:value={draft.truckInput.ndiName} />
          <datalist id="ndis">{#each devices?.ndi ?? [] as n (n.name)}<option value={n.name}></option>{/each}</datalist>
        </label>
      {/if}
      {#if draft.truckInput.kind === 'source-pair'}
        <label>Source
          <select bind:value={draft.truckInput.sourceId}>{#each data.sources as s (s.id)}<option value={s.id}>{s.name}</option>{/each}</select>
        </label>
        <p class="wide muted">Use this when the DeckLink/NDI input already feeds a source: the talkback pair is tapped from that pipeline.</p>
      {/if}
      {#if ['alsa', 'pulse', 'decklink', 'ndi', 'source-pair'].includes(draft.truckInput.kind)}
        {#if draft.truckInput.kind !== 'source-pair'}<label>Input channels <input type="number" bind:value={draft.truckInput.inputChannels} /></label>{/if}
        <label>Channel pair
          <span class="row"><input type="number" style="width:70px" bind:value={draft.truckInput.channelPair![0]} /> + <input type="number" style="width:70px" bind:value={draft.truckInput.channelPair![1]} /></span>
        </label>
      {/if}
    </div>
  </section>

  <section class="panel">
    <h3>Truck talkback output</h3>
    <p class="muted">Mix of everyone's talk (mix-minus of the truck input) for the intercom matrix.</p>
    <div class="form">
      <label class="inline"><input type="checkbox" bind:checked={draft.truckOutput.enabled} /> Enabled</label>
      <label>Gain dB <input type="number" bind:value={draft.truckOutput.gainDb} /></label>
      <label>Mix slots <input type="number" bind:value={draft.truckOutput.slots} /></label>
      <label class="inline"><input type="checkbox" bind:checked={draft.truckOutput.includeDirectors} /> Include director browsers</label>
    </div>
    {#each draft.truckOutput.sinks as s, i (i)}
      <div class="sink form">
        <b>{s.kind}</b>
        {#if s.kind === 'alsa' || s.kind === 'pulse'}
          <label>Device <input list="asink" bind:value={s.device} />
            <datalist id="asink">{#each devices?.audioSinks ?? [] as d (d.device + d.factory)}<option value={d.device}>{d.name} ({d.factory})</option>{/each}</datalist>
          </label>
        {/if}
        {#if s.kind === 'decklink'}
          <label>Device <input type="number" bind:value={s.decklinkDevice} /></label>
          <label>SDI mode <input bind:value={s.decklinkMode} /></label>
        {/if}
        {#if s.kind === 'alsa' || s.kind === 'decklink'}
          <label>Output channels <input type="number" bind:value={s.outputChannels} /></label>
          <label>On pair <span class="row"><input type="number" style="width:64px" bind:value={s.channelPair![0]} /> + <input type="number" style="width:64px" bind:value={s.channelPair![1]} /></span></label>
        {/if}
        {#if s.kind === 'ndi'}<label>NDI name <input bind:value={s.ndiName} /></label>{/if}
        <button class="small danger" onclick={() => draft.truckOutput.sinks.splice(i, 1)}>Remove</button>
      </div>
    {/each}
    <div class="row" style="margin-top:8px">
      <span class="muted">Add output:</span>
      <button class="small" onclick={() => addSink('alsa')}>ALSA</button>
      <button class="small" onclick={() => addSink('pulse')}>PipeWire</button>
      <button class="small" onclick={() => addSink('decklink')}>DeckLink SDI</button>
      <button class="small" onclick={() => addSink('ndi')}>NDI</button>
    </div>
  </section>
</div>
{#if error}<p class="error">{error}</p>{/if}
{#if msg}<p class="muted">{msg}</p>{/if}
<div class="row" style="margin-top:12px">
  <button class="primary" onclick={save}>Save &amp; apply</button>
  <button onclick={() => (draft = clone(data.audio))}>Revert</button>
</div>

<style>
  .sink { margin-top: 10px; padding-top: 10px; border-top: 1px solid var(--line); }
  .x { padding: 0 4px; border: 0; background: none; }
</style>
